import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, FormikProps } from 'formik'
import { Button } from 'primereact/button'
import {
  Column,
  ColumnBodyOptions,
  ColumnEditorOptions,
} from 'primereact/column'
import {
  DataTableDataSelectableEvent,
  DataTableEditingRows,
  DataTableRowEditCompleteEvent,
  DataTableRowEditEvent,
  DataTableRowEditSaveEvent,
  DataTableRowEditValidatorOptions,
  DataTableValue,
  DataTableValueArray,
} from 'primereact/datatable'
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'

import { NumberBodyTemplate } from '../../../../../components/tableTemplates'
import {
  DeliveryForecastDespatchAdviceItem,
  DeliveryForecastDespatchAdviceItemBatch,
} from '../../../../../data/models'
import {
  ConfigurableComponent,
  ConfigurableTable,
} from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config'
import { FormFieldName, FormValues } from './ConfirmationBatchesTable.form'

type Props = {
  formik: FormikProps<FormValues>
  batches?: DeliveryForecastDespatchAdviceItemBatch[]
  isLoading: boolean
  hasLoadingRowId: (rowId: string) => boolean
  isBulkDeleteRunning: boolean
  handleCreate: (
    batch: DeliveryForecastDespatchAdviceItemBatch,
  ) => Promise<void>
  handleSave: (
    formData: DeliveryForecastDespatchAdviceItemBatch,
  ) => Promise<void>
  handleDelete: (
    batches: DeliveryForecastDespatchAdviceItemBatch[],
    callback?: () => void,
  ) => Promise<void>

  isFormFieldValid: (index: number, name: FormFieldName) => boolean
  getFormErrorMessage: (
    index: number,
    name: FormFieldName,
  ) => false | JSX.Element
}

export const ConfirmationBatchesTableView: React.FC<Props> = ({
  formik,
  batches,
  isLoading,
  hasLoadingRowId,
  isBulkDeleteRunning,
  handleCreate,
  handleSave,
  handleDelete,

  isFormFieldValid,
  getFormErrorMessage,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  const [editingRows, setEditingRows] = useState<DataTableEditingRows>({})
  const [selectedBatches, setSelectedBatches] =
    useState<DeliveryForecastDespatchAdviceItem['ConfirmationBatches']>()

  const isEditingMode = Object.keys(editingRows).length > 0
  const hasNoSelectedRows =
    !selectedBatches || selectedBatches?.length === 0 ? true : false

  const handleAddNewRow = async () => {
    const newRowIndex = formik.values.confirmationBatches?.length || 0
    const newRowData = {
      Id: `newBatch-${newRowIndex}`,
      Batchnumber: '',
      Quantity: 0,
    }

    await formik.setFieldValue(`confirmationBatches.${newRowIndex}`, newRowData)
    setEditingRows(prev => ({ ...prev, [newRowData.Id]: true }))
  }

  const handleDeleteRows = async (
    batches: DeliveryForecastDespatchAdviceItemBatch[],
  ) => {
    if (batches) {
      await handleDelete(batches, () => setSelectedBatches([]))
    }
  }

  const headerTitle = t(
    'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.title',
  )
  const headerActions = (
    <>
      <Button
        label={t(
          'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.delete.label',
        )}
        onClick={() => handleDeleteRows(selectedBatches!)}
        icon="pi pi-trash"
        outlined
        disabled={hasNoSelectedRows || isEditingMode}
        loading={isBulkDeleteRunning}
      />

      <Button
        label={t(
          'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.add.label',
        )}
        onClick={handleAddNewRow}
        icon="pi pi-plus"
        outlined
        disabled={isEditingMode}
      />
    </>
  )

  // batchnumberEditor
  const batchnumberEditor = (options: ColumnEditorOptions) => {
    const batchnumberComponent = (
      <InputText
        id="Batchnumber"
        type="text"
        autoFocus
        className="w-full"
        value={
          formik.values.confirmationBatches?.[options.rowIndex].Batchnumber
        } // {options.value}
        onChange={event => {
          const value = event.target.value || ''
          options.editorCallback!(value)

          formik.setFieldValue(
            `confirmationBatches.${options.rowIndex}.Batchnumber`,
            value,
          )
        }} // {(e: React.ChangeEvent<HTMLInputElement>) => options.editorCallback!(e.target.value)}
      />
    )

    const batchnumberErrorMsg = (
      <div>{getFormErrorMessage(options.rowIndex, 'Batchnumber')}</div>
    )

    const batchnumberWrapper = (
      <ConfigurableComponent
        key={`batchnumber-${options.rowIndex}`}
        config={config.deliveryForecastDespatchAdvice.addConfirmationBatch}
        component={batchnumberComponent}
        errorMsg={batchnumberErrorMsg}
        className="m-0"
      />
    )

    return <>{batchnumberWrapper}</>
  }

  // quantityEditor
  const quantityEditor = (options: ColumnEditorOptions) => {
    const quantityComponent = (
      <InputNumber
        id="Quantity"
        className="w-full"
        value={formik.values.confirmationBatches?.[options.rowIndex].Quantity} // {options.value} //{formik.values.confirmationBatches?.[options.rowIndex].Quantity}
        onChange={(event: InputNumberChangeEvent) => {
          options.editorCallback!(event.value)

          formik.setFieldValue(
            `confirmationBatches.${options.rowIndex}.Quantity`,
            event.value,
          )
        }}
        locale={language}
        showButtons
        min={0}
      />
    )

    const quantityErrorMsg = (
      <div>{getFormErrorMessage(options.rowIndex, 'Quantity')}</div>
    )

    const quantityWrapper = (
      <ConfigurableComponent
        config={config.deliveryForecastDespatchAdvice.addConfirmationBatch}
        component={quantityComponent}
        errorMsg={quantityErrorMsg}
        className="m-0"
      />
    )

    return <>{quantityWrapper}</>
  }

  //  Action template
  const actionBodyTemplate = (
    rowData: DeliveryForecastDespatchAdviceItemBatch,
    options: ColumnBodyOptions,
  ) => {
    if (options.rowEditor?.editing)
      return (
        <>
          <Button
            icon="pi pi-times"
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onCancelClick) {
                options.rowEditor.onCancelClick(event)
              }
            }}
          />

          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onSaveClick) {
                options.rowEditor.onSaveClick(event)
              }
            }}
          />
        </>
      )

    return (
      <>
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text"
          severity="danger"
          onClick={async () => {
            await handleDelete([rowData], () =>
              setSelectedBatches(prevSelectedBatches =>
                prevSelectedBatches?.filter(batch => batch.Id !== rowData.Id),
              ),
            )
          }}
          loading={hasLoadingRowId(rowData.Id)}
          disabled={isEditingMode}
        />

        <Button
          icon="pi pi-clone"
          className="p-button-rounded p-button-text"
          onClick={() => handleDuplicate(rowData)}
          loading={hasLoadingRowId(rowData.Id)}
          disabled={isEditingMode}
        />

        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={event => {
            if (options.rowEditor?.onInitClick) {
              options.rowEditor.onInitClick(event)
            }
          }}
          loading={hasLoadingRowId(rowData.Id)}
          disabled={isEditingMode}
        />
      </>
    )
  }

  const buttons = (
    <Column
      rowEditor
      key="buttons"
      body={(e, options) => actionBodyTemplate(e, options)}
      align="right"
    ></Column>
  )

  const handleDuplicate = (
    rowData: DeliveryForecastDespatchAdviceItemBatch,
  ) => {
    const newRowData = {
      ...rowData,
      Id: `newBatch-${formik.values.confirmationBatches?.length}`,
    }

    const newBatches = [...formik.values.confirmationBatches!, newRowData]

    formik.setFieldValue('confirmationBatches', newBatches)

    setEditingRows(prev => ({
      ...prev,
      [newRowData.Id]: true,
    }))
  }

  const onRowEditInit = (event: DataTableRowEditEvent) => {}

  const onRowEditChange = (event: DataTableRowEditEvent) => {
    setEditingRows(event.data)
  }

  const onRowEditCancel = (event: DataTableRowEditEvent) => {
    // If the batch is newly added it has the id "newBatch-X" we need to remove this from formik.
    // Otherwise it was already added but the user wants to cancel the editing and remove the changes.
    const isNewBatch = event.data.Id === `newBatch-${event.index}`
    if (isNewBatch) {
      const updatedBatches = formik.values.confirmationBatches?.filter(
        batch => batch.Id !== event.data.Id,
      )
      formik.setFieldValue('confirmationBatches', updatedBatches)
    } else {
      const originalBatch =
        formik.initialValues.confirmationBatches?.[event.index]
      formik.setFieldValue(`confirmationBatches.${event.index}`, originalBatch)
    }
  }

  const rowEditValidator = (
    data: DataTableValue,
    options: DataTableRowEditValidatorOptions<DataTableValueArray>,
  ) => {
    const rowErrors =
      Array.isArray(formik.errors) &&
      (formik.errors[
        options.rowIndex
      ] as FormikErrors<DeliveryForecastDespatchAdviceItemBatch>)

    const hasErrors = rowErrors && Object.keys(rowErrors).length > 0
    const isValid = !hasErrors

    return isValid
  }

  const onRowEditSave = (
    event: DataTableRowEditSaveEvent<DataTableValueArray>,
  ) => {
    formik.handleSubmit()
  }

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    const { newData, index } = event

    formik.setFieldValue(
      `confirmationBatches.${index}.Batchnumber`,
      newData.Batchnumber,
    )
    formik.setFieldValue(
      `confirmationBatches.${index}.Quantity`,
      newData.Quantity,
    )
    formik.validateForm()

    const isNewBatch = newData.Id === `newBatch-${event.index}`

    if (isNewBatch) {
      handleCreate(newData as DeliveryForecastDespatchAdviceItemBatch)
    } else {
      handleSave(newData as DeliveryForecastDespatchAdviceItemBatch)
    }

    setEditingRows({})
  }

  const isDataSelectable = (
    event: DataTableDataSelectableEvent,
  ): boolean | null => {
    const isNewItem = event.data.Id === `newBatch-${event.index}`
    return !isNewItem
  }

  return (
    <ConfigurableTable
      config={
        config.deliveryForecastDespatchAdvice.articleDetail.confirmationBatches
          .table
      }
      data={formik.values.confirmationBatches}
      headerTitle={headerTitle}
      headerActions={headerActions}
      isLoading={isLoading}
      stateKey=""
      buttons={buttons}
      checkboxen
      isDataSelectable={isDataSelectable}
      selected={selectedBatches}
      setSelected={setSelectedBatches}
      dataKey="Id"
      editMode="row"
      editingRows={editingRows}
      // Callbacks
      onRowEditInit={onRowEditInit}
      onRowEditChange={onRowEditChange}
      onRowEditCancel={onRowEditCancel}
      rowEditValidator={rowEditValidator}
      onRowEditSave={onRowEditSave}
      onRowEditComplete={onRowEditComplete}
      className="mt-4"
    >
      <Column
        key="batchnumber"
        field="Batchnumber"
        header="Batchnumber"
        editor={options => batchnumberEditor(options)}
      />
      <Column
        key="quantity"
        field="Quantity"
        header="Quantity"
        editor={options => quantityEditor(options)}
        body={(rowData: DeliveryForecastDespatchAdviceItemBatch) =>
          NumberBodyTemplate(rowData.Quantity)
        }
      />
    </ConfigurableTable>
  )
}
